import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Sparkles, Send, Loader2, CheckCircle, AlertTriangle, Edit, Save } from 'lucide-react';

const AIRuleBuilder = ({ onRuleCreated }) => {
  const [instruction, setInstruction] = useState('');
  const [parsedRule, setParsedRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingJson, setEditingJson] = useState(false);
  const [jsonRule, setJsonRule] = useState('');

  const API = (process.env.REACT_APP_BACKEND_URL || "") || 'http://localhost:8001';

  const parseNaturalLanguage = async () => {
    if (!instruction.trim()) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(`${API}/api/ai/parse-rule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          instruction: instruction.trim(),
          context: 'trial_balance_mapping'
        })
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setParsedRule(result.parsed_rule);
        setJsonRule(JSON.stringify(result.parsed_rule, null, 2));
        setSuccess('Rule parsed successfully! Review and save when ready.');
      } else {
        throw new Error(result.message || result.detail || 'Failed to parse rule');
      }
    } catch (err) {
      console.error('Error parsing rule:', err);
      setError(`Failed to parse rule: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveRule = async () => {
    if (!parsedRule) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const ruleToSave = editingJson ? JSON.parse(jsonRule) : parsedRule;
      
      const response = await fetch(`${API}/api/rules/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...ruleToSave,
          created_via: 'natural_language',
          original_instruction: instruction
        })
      });
      
      const result = await response.json();
      
      if (result.status === 'success' || response.ok) {
        setSuccess('Rule saved successfully!');
        setInstruction('');
        setParsedRule(null);
        setJsonRule('');
        setEditingJson(false);
        
        if (onRuleCreated) {
          onRuleCreated(ruleToSave);
        }
      } else {
        throw new Error(result.message || 'Failed to save rule');
      }
    } catch (err) {
      console.error('Error saving rule:', err);
      setError(`Failed to save rule: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleJsonEditor = () => {
    if (!editingJson && parsedRule) {
      setJsonRule(JSON.stringify(parsedRule, null, 2));
    } else if (editingJson) {
      try {
        const parsed = JSON.parse(jsonRule);
        setParsedRule(parsed);
      } catch (err) {
        setError('Invalid JSON format. Please fix the JSON structure.');
        return;
      }
    }
    setEditingJson(!editingJson);
  };

  const clearForm = () => {
    setInstruction('');
    setParsedRule(null);
    setJsonRule('');
    setEditingJson(false);
    setError(null);
    setSuccess(null);
  };

  const exampleInstructions = [
    "Map all facilities accounts to overhead pool",
    "Accounts starting with 6000 should go to G&A",
    "Flag any amount over $10,000 for review",
    "All rent and lease expenses go to intermediate pool",
    "Computer equipment goes to overhead, office supplies to G&A",
    "Salaries and wages should be allocated to fringe pool"
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-semibold">AI Rule Builder</h3>
            </div>
            {parsedRule && (
              <Button variant="outline" size="sm" onClick={clearForm}>
                New Rule
              </Button>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Describe your mapping rule in plain English and let AI create the technical implementation
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Natural Language Input */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Describe your rule in plain English:
            </label>
            <textarea
              placeholder="Examples:
• Map all facilities accounts to overhead pool
• Accounts starting with 6000 should go to G&A  
• Flag any amount over $10,000 for review
• All rent and lease expenses go to intermediate pool"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px] resize-y"
            />
          </div>
          
          {/* Example Instructions */}
          <div>
            <label className="text-sm font-medium mb-2 block">Quick examples:</label>
            <div className="flex flex-wrap gap-2">
              {exampleInstructions.map((example, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => setInstruction(example)}
                  className="text-xs"
                >
                  {example}
                </Button>
              ))}
            </div>
          </div>
          
          <Button 
            onClick={parseNaturalLanguage}
            disabled={!instruction.trim() || loading}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> 
                Processing with AI...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" /> 
                Parse Rule with GPT-4o
              </>
            )}
          </Button>

          {/* Success Message */}
          {success && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {success}
              </AlertDescription>
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Parsed Rule Display */}
          {parsedRule && (
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-blue-800">
                    AI Understood This Rule:
                  </h4>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      Confidence: {parsedRule.confidence ? `${(parsedRule.confidence * 100).toFixed(0)}%` : 'N/A'}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleJsonEditor}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      {editingJson ? 'Visual' : 'JSON'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!editingJson ? (
                  // Visual Rule Display
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong className="text-blue-800">Rule Name:</strong>
                      <span className="ml-2">{parsedRule.rule_name || 'Unnamed Rule'}</span>
                    </div>
                    
                    {parsedRule.conditions && parsedRule.conditions.length > 0 && (
                      <div>
                        <strong className="text-blue-800">Conditions:</strong>
                        <ul className="ml-4 mt-1 space-y-1">
                          {parsedRule.conditions.map((condition, i) => (
                            <li key={i} className="bg-white p-2 rounded border">
                              If <span className="font-medium">{condition.field}</span>{' '}
                              <span className="text-blue-600">{condition.operator}</span>{' '}
                              "<span className="font-medium">{condition.value}</span>"
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {parsedRule.actions && parsedRule.actions.length > 0 && (
                      <div>
                        <strong className="text-blue-800">Actions:</strong>
                        <ul className="ml-4 mt-1 space-y-1">
                          {parsedRule.actions.map((action, i) => (
                            <li key={i} className="bg-white p-2 rounded border">
                              Then <span className="text-green-600">{action.action_type || action.type}</span>{' '}
                              to <span className="font-medium">{action.target_pool || action.target}</span>
                              {action.confidence && (
                                <span className="text-gray-500">
                                  {' '}(Confidence: {(action.confidence * 100).toFixed(0)}%)
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {parsedRule.scope && (
                      <div>
                        <strong className="text-blue-800">Scope:</strong>
                        <span className="ml-2 capitalize">{parsedRule.scope}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  // JSON Editor
                  <div>
                    <textarea
                      value={jsonRule}
                      onChange={(e) => setJsonRule(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-xs min-h-[200px]"
                      placeholder="Edit rule JSON..."
                    />
                  </div>
                )}
                
                <div className="flex gap-2 mt-4">
                  <Button onClick={saveRule} disabled={loading} className="flex-1">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Rule
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={clearForm} className="flex-1">
                    Start Over
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIRuleBuilder;
